/* eslint-disable react/jsx-props-no-spreading */
import React, { memo, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { PaperProps } from "@mui/material";

import { ColumnInstance, TableToggleHideAllColumnProps } from "react-table";

import { Popup, TransferList, TransferListProps } from "components/core";

import { ColumnView, TransferListValue } from "types/global";

import {
  useCreateCustomerHeader,
  useGetCustomerHeadersById,
  useUpdateCustomerHeaders,
} from "hooks/useHeader";

import { EXPANDER_ACCESSOR_ID, HeaderType, SELECTION_ACCESSOR_ID, TableListType } from ".";

const mapToTransferValueList = (data: string[]): TransferListValue[] =>
  data?.map((item) => ({ name: item, value: item }));

const allSkuList: TransferListProps["defaultLeft"] = mapToTransferValueList([
  "wicNumber",
  "deptNum",
  "vendorNum",
  "concept",
  "usStatus",
  "euStatus",
  "ukStatus",
  "glbStatus",
  "qcStatus",
  "collection",
  "frame",
  "colorCode",
  "sizeCode",
  "fabric",
  "fill",
  "finish",
  "function",
  "bom",
  "skuStatus",
  "skuCreateDate",
  "startSellingDate",
  "buyerDesc",
  "subDeptDesc",
  "vendorItemDesc",
  "vendorStyle",
  "countryOfOrigin",
  "usMinPoCancelDate",
  "euMinPoCancelDate",
  "ukMinPoCancelDate",
  "glbMinPoCancelDate",
  "countryCodeList",
]);

const newSkuList: TransferListProps["defaultLeft"] = mapToTransferValueList([
  "wicNumber",
  "exportMarket",
  "skuTestLevels",
  "collection",
  "frame",
  "colorCode",
  "sizeCode",
  "skuCreateDate",
  "startSellingDate",
  "buyerDesc",
  "subDeptDesc",
  "vendorItemDesc",
  "vendorStyle",
  "fabric",
  "fill",
  "finish",
  "function",
  "bom",
  "countryOfOrigin",
  "skuStatus",
  "minPoCancelDate",
  "countryCodeList",
  "dcList",
  "deptNum",
  "vendorNum",
  "concept",
]);

const submissionList: TransferListProps["defaultLeft"] = mapToTransferValueList([
  "submissionNumber",
  "wicNumber",
  "exportMarket",
  "skuTestLevels",
  "status",
  "vendorNum",
  "requestedDate",
  "ecd",
  "lab",
]);

interface ManageColumnPopupProps extends Omit<PaperProps, "onChange" | "onSubmit"> {
  open: boolean;
  isEditColumnViewType: boolean;
  title: string;
  submitText: string;
  allCoumns: ColumnInstance<object>[];
  getToggleHideAllColumnsProps: (
    props?: Partial<TableToggleHideAllColumnProps>,
  ) => TableToggleHideAllColumnProps;
  tableType?: TableListType;
  headerId?: string;
  onClose: () => void;
  onSubmit: (columnIds: string[]) => void;
}

function ManageColumnPopup({
  sx,
  title = "Create Column View",
  submitText = "Submit",
  headerId = "",
  open,
  allCoumns = [],
  isEditColumnViewType,
  getToggleHideAllColumnsProps,
  tableType,
  onClose,
  onSubmit,
}: ManageColumnPopupProps) {
  const navigate = useNavigate();

  const [defaultLeftList, setDefaultLeftList] = useState<TransferListValue[] | undefined>([]);
  const [defaultRightList, setDefaultRightList] = useState<TransferListValue[]>([]);
  const [defaultList, setDefaultList] = useState<TransferListValue[] | undefined>([]);
  const [currentSavedList, setCurrentSavedList] = useState<TransferListValue[] | undefined>([]);
  const [columnView, setColumnView] = useState<string>("My View");

  const { isLoading: createHeaderLoading, refetch: createHeader } = useCreateCustomerHeader();
  const { isLoading: updateHeaderLoading, refetch: updateHeader } = useUpdateCustomerHeaders();

  const {
    data: headerData,
    isLoading: fetchLoading,
    refetch: fetchHeaderById,
  } = useGetCustomerHeadersById(headerId);

  const isDataLoading = createHeaderLoading || updateHeaderLoading || fetchLoading;

  useEffect(() => {
    if (headerId && headerId !== "") {
      fetchHeaderById({}).then(({ data }) => {
        if (data.code === 200) {
          if (data?.data?.header) {
            const valueList = mapToTransferValueList(data.data.header.split(","));
            setDefaultRightList(valueList);
            setCurrentSavedList(valueList);
          }
        }
      });
    } else {
      setDefaultRightList([]);
    }
  }, [headerId]);

  useEffect(() => {
    if (!headerData) return;

    setColumnView(headerData?.name);
  }, [headerData]);

  useEffect(() => {
    let tempDefaultList;
    if (tableType === "new_skus") {
      tempDefaultList = newSkuList;
    } else if (tableType === "all_skus") {
      tempDefaultList = allSkuList;
    } else if (tableType === "submissions") {
      tempDefaultList = submissionList;
    }

    setDefaultList(tempDefaultList || []);
  }, []);

  useEffect(() => {
    let tempDefaultLeftList;
    if (headerData?.header && headerData?.header !== "") {
      tempDefaultLeftList = defaultList?.filter(
        (val) => !headerData?.header.split(",").includes(val?.value),
      );
    }

    setDefaultLeftList(isEditColumnViewType ? tempDefaultLeftList : defaultList);
  }, [tableType, headerData, isEditColumnViewType, defaultList]);

  // filter out helper accessors
  const optOutHelperColumns = allCoumns.filter(
    (col) => ![SELECTION_ACCESSOR_ID, EXPANDER_ACCESSOR_ID].includes(col?.id),
  );

  const handleClose = () => {
    if (defaultRightList?.length) setDefaultRightList([]);

    onClose();
  };

  const handleSubmit = async () => {
    if (!defaultRightList?.length) return;

    const type =
      tableType === "all_skus"
        ? HeaderType.AllSku
        : tableType === "new_skus"
        ? HeaderType.NewSku
        : HeaderType.Submission;

    const submitData: Partial<ColumnView> = {
      header: defaultRightList?.map((item) => item?.value)?.toString(),
      isCurrent: true,
      name: columnView,
      type,
      id: parseInt(headerId, 10),
    };

    if (headerId && headerId !== "") {
      await updateHeader(submitData);
    } else {
      const { id, ...createPayload } = submitData;

      await createHeader(createPayload);
    }

    await handleClose();
    await navigate(0);
  };

  const handleCheckedProperties = ({
    left,
    right,
  }: {
    left: TransferListValue[];
    right: TransferListValue[];
  }) => {
    setDefaultRightList(right);
  };

  const handleColumnViewChange = (value: string) => {
    setColumnView(value);
  };

  return (
    <Popup
      maxWidth="md"
      title={title}
      submitText={submitText}
      loading={isDataLoading}
      submitDisabled={isDataLoading}
      open={open}
      onClose={handleClose}
      onSubmit={handleSubmit}
    >
      {!fetchLoading && (
        <TransferList
          actionRequired={{
            defaultView: "right",
            clearSelected: "right",
          }}
          allCheckDisabled
          leftText="Unselected Properties"
          rightText="Selected Properties"
          columnView={columnView}
          isAsyncData
          isNameAsKey
          defaultLeft={defaultLeftList}
          defaultRight={defaultRightList}
          currentSavedList={currentSavedList}
          defaultListLength={defaultList?.length}
          loading={isDataLoading}
          onUpdate={(checked) => handleCheckedProperties(checked)}
          onInputChange={handleColumnViewChange}
        />
      )}
    </Popup>
  );
}

export default memo(ManageColumnPopup);
